import React, {useMemo} from 'react';
import {Country} from '@/Pages/Countries/store/types';
import ErrorSection from '../ErrorSection/ErrorSection';
import {ErrorItem} from '../ErrorSection/ErrorSection.types';
import {useInvalidCostFactorHelpers} from '../useInvalidCostFactorHelpers';
import {
  isCountryRulesErrorCode,
  getCountryRulesError,
  CountryRulesErrorCode
} from '../InvalidCostFactorsPopup.utils';
import {CountryRulesErrorsSectionProps} from './CountryRulesErrorsSection.types';

const CountryRulesErrorsSection: React.FC<CountryRulesErrorsSectionProps> = ({
  invalidCostFactors,
  busValues,
  busPartners,
  countries,
  costYear
}) => {
  const {getVehicleSettings, countriesMap, logMissing} =
    useInvalidCostFactorHelpers(busValues, busPartners, countries);

  const countryRulesErrors = useMemo(() => {
    if (!costYear) return [];

    const errors = (invalidCostFactors ?? []).filter(invalidCostFactor =>
      isCountryRulesErrorCode(invalidCostFactor.errorCode)
    );
    const countryRulesToErrorCodes = new Map<string, CountryRulesErrorCode[]>();

    const groupedCountryRulesErrors: Map<
      string,
      {countryRules: Country; errors: ErrorItem[]}
    > = new Map();

    for (const error of errors) {
      const vehicleSettings = getVehicleSettings(error);
      if (!vehicleSettings) continue;

      if (!vehicleSettings.countryRulesCountryId) {
        logMissing('country rules country id', error);
        continue;
      }

      const countryRules = countriesMap.get(
        vehicleSettings.countryRulesCountryId
      );
      if (!countryRules) {
        logMissing('country rules', error);
        continue;
      }

      // Avoid duplicates
      const prevErrorCodes =
        countryRulesToErrorCodes.get(countryRules.id) ?? [];
      const currErrorCode = error.errorCode as CountryRulesErrorCode;
      if (prevErrorCodes.includes(currErrorCode)) continue;
      countryRulesToErrorCodes.set(countryRules.id, [
        ...prevErrorCodes,
        currErrorCode
      ]);

      const errorMessage = getCountryRulesError(
        currErrorCode,
        countryRules,
        costYear
      );

      const group = groupedCountryRulesErrors.get(countryRules.id) ?? {
        countryRules,
        errors: [] as ErrorItem[]
      };
      group.errors.push({code: currErrorCode, message: errorMessage});
      groupedCountryRulesErrors.set(countryRules.id, group);
    }

    return Array.from(groupedCountryRulesErrors.values());
  }, [
    costYear,
    invalidCostFactors,
    getVehicleSettings,
    countriesMap,
    logMissing
  ]);

  if (countryRulesErrors.length === 0) return null;

  return (
    <>
      {countryRulesErrors.map(({countryRules, errors: errorList}) => (
        <ErrorSection
          key={`country-rules-${countryRules.id}`}
          id={`country-rules-${countryRules.id}`}
          title={
            <>The following {countryRules.name} country rules are missing:</>
          }
          errors={errorList}
        />
      ))}
    </>
  );
};

export default React.memo(CountryRulesErrorsSection);
